import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useTicker from '@hooks/useTicker'
import { useReportQueue } from '@hooks/useReportQueue'
import ConfirmModal from '@components/common/ConfirmModal'
import QueueStateTabs from '@components/reports/queue/QueueStateTabs'
import QueueScopeSelect from '@components/reports/queue/QueueScopeSelect'
import QueueTable from '@components/reports/queue/QueueTable'
import ReportJobDrawer from '@components/reports/queue/ReportJobDrawer'
import ReportBand from '@components/reports/ReportBand'
import JobEmptyState from '@components/reports/queue/JobEmptyState'
import QueueReportModal from '@components/reports/queue/QueueReportModal'
import QueueCatalogUnreadable from '@components/reports/queue/QueueCatalogUnreadable'
import { useQueueReportPicker } from '@components/reports/queue/useQueueReportPicker'
import ManageSchedulesLink from '@components/reports/queue/ManageSchedulesLink'
import UpcomingScheduledRuns from '@components/reports/queue/UpcomingScheduledRuns'
import reportJobService, { mergeJob, preferListDelivery } from '@api/v1/reportJobService'
import { jobDisplayName, scheduleNameMap, scheduleRetainMap } from '@components/reports/queue/jobIdentity'
import { queueRetentionSummary } from '@components/reports/queue/resultRetention'
import { toast } from '@store/toastStore'
import { fmtInFuture } from '@components/reports/queue/queueFormat'
import { useSchedulesQuery } from '@hooks/useSchedules'
import { ACTIVE_STATES, QUEUE_PAGE_SIZE } from '@config/queueConfig'

const REPORT_JOBS = import.meta.env.VITE_REPORT_JOBS === 'true'

/** The `?days=` window used when the URL doesn't name a usable one. */
const DEFAULT_WINDOW_DAYS = 7

/**
 * The unbounded window (QA, 2026-09-03: "Filter does not have an 'All' option").
 *
 * `Infinity` is the value, not a flag, and that is the point: the cutoff below
 * becomes `-Infinity`, every row passes, and the filter memo needs **no branch at
 * all**. Its boundary behaviour — the part that is mutation-tested — is untouched.
 *
 * `'all'` was previously a deliberately-REJECTED `?days=` value, pinned by a test
 * alongside `abc` / `7d` / `0` / `-5`: `Number('all')` is NaN, `x >= NaN` is false
 * for every x, so it used to empty the whole queue behind a "you have no runs"
 * panel. Only `'all'` is promoted out of that guard here; the four genuinely
 * malformed values must keep falling back to 7.
 */
const ALL_WINDOW_PARAM = 'all'
const ALL_WINDOW = Infinity

/**
 * Screen 1 — the Queue tab. A first-class Report Center destination alongside
 * Dashboards and Reports, where long-running and (later) scheduled runs live.
 *
 * All view state is in the URL (?state=, ?q=, ?scope=, ?jobId=) per project
 * convention, so a queue view is shareable and survives back/forward.
 *
 * Deliberately NO stat-tile row above the table — the counts live in the state
 * tabs plus one quiet subtitle line, so the summary never competes with the
 * table where people actually spend their time.
 */
const ReportQueue = () => {
  useDocumentTitle('Report Queue')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = searchParams.get('state') || 'all'
  const search = searchParams.get('q') || ''
  const scope = searchParams.get('scope') || 'mine'
  const openJobId = searchParams.get('jobId')
  const [confirmDismiss, setConfirmDismiss] = useState(null)

  /**
   * The time window, in days, as a NUMBER — validated, because it comes straight
   * off the URL and this page's whole view state is deliberately shareable, so it
   * is not necessarily one of the three values the select offers.
   *
   * Measured 2026-08-28, this was reachable and silent: `Number('all')`,
   * `Number('abc')` and `Number('7d')` are all NaN, `cutoff` is then NaN, every
   * `x >= NaN` is false, and the page rendered an EMPTY QUEUE byte-identical to
   * "you have no runs" — this repo's documented failed-read-as-absence class. Three
   * things made it worse than a bad filter usually is: the `<select>` had no option
   * matching the value so it rendered BLANK and gave no clue what was applied; the
   * empty state's "Clear filter" button clears `state` and `q` and has never
   * touched `days`, so there was no way out on screen; and `?days=all` is the exact
   * guess a person makes on a codebase whose wire vocabulary includes `$$cb_all`.
   * `?days=0` and `?days=-5` do the same thing without going through NaN — the
   * cutoff lands at or after the fetch time, so every past run is excluded.
   *
   * `?days=` (empty) was never affected: `Number('')` is 0, but the old
   * `searchParams.get('days') || '7'` caught the empty string first.
   *
   * Anything that is not a positive finite number falls back to the default. A
   * hand-built `?days=90` still works exactly as it did — this narrows nothing that
   * ever produced a usable view.
   */
  const daysParam = searchParams.get('days')
  const windowDays = useMemo(() => {
    // The sentinel is checked BEFORE the finite test, because `Number('all')` is
    // NaN and would otherwise fall into the malformed-value fallback below.
    if (daysParam === ALL_WINDOW_PARAM) return ALL_WINDOW
    const n = Number(daysParam)
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_WINDOW_DAYS
  }, [daysParam])

  // Schedules, for the "Manage schedules" control (spec 074). Two reasons this is
  // worth a query on the Queue page rather than living only on the schedules page:
  //
  //  1. A schedule that has gone dormant (`needsSignIn`) says so ONLY on the
  //     schedules page, which is one click behind this button — so the state the
  //     whole credential design exists to make honest was effectively invisible.
  //  2. A new schedule produces no queue row until it first fires (a weekly one,
  //     up to a week later), so the Queue looked empty right after creating one.
  //
  // Declared through the shared hook rather than a second inline useQuery: this
  // page and the schedules page were each carrying their own key literal and
  // their own staleTime, which is how the count and the list could disagree.
  //
  // Spec 132 — declared ABOVE `useReportQueue` because the same read now also names
  // each run's schedule ("from Billable Hours Report") and says when an owned run's
  // schedule has been deleted. No new request: this is the query the page already made.
  const schedulesQuery = useSchedulesQuery({ enabled: REPORT_JOBS })
  /*
   * The name map is keyed on react-query's own `data` reference, and is the module's
   * EMPTY_MAP whenever the read has not resolved — NEVER on the flattened
   * `schedulesQuery.schedules`, which is a fresh `[]` on every render while the read is
   * loading, failed or paused (`useSchedules.js`). A map rebuilt per render would
   * rebuild `nameJob`, `allJobs` and the open job with it, and the drawer would re-run
   * its effects every poll tick.
   *
   * `schedulesResolved` is the first licence to read a missing schedule id as DELETED:
   * a completed read of the caller's OWN schedules. Absence from an unresolved read
   * says nothing, and the drawer then keeps its ordinary retention wording.
   *
   * It is not the only one. This list is read on mount and NOT polled, while the job
   * list polls every 5 s, so a resolved list can predate a schedule — and a run —
   * created since. `schedulesReadAt` (react-query's `dataUpdatedAt`, handed on only
   * with a resolved read) lets `nameJob` refuse to infer anything about a run newer
   * than the read; it also refuses a list cut at cb-api's row cap (`jobIdentity.js`).
   */
  const schedulesResolved = schedulesQuery.ready && schedulesQuery.scope === 'mine'
  const schedulesData = schedulesResolved ? schedulesQuery.data : undefined
  const schedulesReadAt = schedulesResolved ? schedulesQuery.dataUpdatedAt : null
  const scheduleNames = useMemo(() => scheduleNameMap(schedulesData?.schedules), [schedulesData])
  // Spec 152 — each schedule's "last N", so a run's retention sentence can name it.
  const scheduleRetains = useMemo(() => scheduleRetainMap(schedulesData?.schedules), [schedulesData])

  const { jobs: tabJobs, allJobs, counts, loading, dismiss, scopeResolved, refresh, error: listError, total, countsAreComplete, countsResolved, fetchedAt, nameJob, resultMaxAgeDays } =
    useReportQueue({ tab, search, scope, scheduleNames, scheduleRetains, schedulesResolved, schedulesReadAt })

  // The drawer still wants a clock, but this page must NOT re-render at 1 Hz
  // unconditionally: the table's `columns` memo and the modal's focus trap both
  // used to churn off it (see useTicker). So it ticks only while something is
  // actually in flight, and the table's cells now own their own clocks.
  const anyActive = useMemo(() => allJobs.some((j) => ACTIVE_STATES.has(j.effectiveState)), [allJobs])
  const now = useTicker(anyActive, 1000)

  // Time window filters the runs already loaded — the list endpoint returns the
  // QUEUE_PAGE_SIZE most recent with no date parameter, so this cannot reach further
  // back. (It could once the cursor is wired through; the server already returns one.)
  //
  // The boundary is anchored to `fetchedAt` — the moment the list ON SCREEN was read
  // from the server — not to `Date.now()` at render. That was the change (2026-08-28);
  // the previous note here explaining an `eslint-disable react-hooks/purity` is
  // SUPERSEDED and deleted with the suppression, because the impurity is gone rather
  // than silenced: every value this memo reads is now a value it depends on.
  //
  // "Last N days" therefore means "as of the list you are looking at", which is both
  // what the reader assumes and strictly more correct than a render clock that can
  // disagree with the data by an arbitrary amount. Observable behaviour is preserved:
  // the memo already re-ran only when `tabJobs` or `windowDays` changed, so a tab left
  // open for an hour never moved the boundary, and it still doesn't. On a refetch
  // `fetchedAt` moves, the memo re-runs, the window refreshes — as before.
  //
  // The two alternatives, and why not:
  //   · Freeze-at-mount (`useState(() => Date.now())`) — a tab open across a refresh
  //     would keep a stale boundary while showing fresh rows.
  //   · Clock-driven (`useTicker`) — rows would vanish under the reader mid-read, on
  //     the very surface where CC-50231 already produced a misattribution defect, and
  //     it reintroduces the 1 Hz re-render the note at :50-53 exists to prevent.
  //
  // `fetchedAt` is null only before the first successful read, when `tabJobs` is
  // necessarily empty (nothing but a successful read populates it) — so passing the
  // list through unfiltered there cannot show a row the window would have hidden.
  const jobs = useMemo(() => {
    if (fetchedAt == null) return tabJobs
    const cutoff = fetchedAt - windowDays * 86_400_000
    return tabJobs.filter((j) => (j.createdAt ?? 0) >= cutoff)
  }, [tabJobs, windowDays, fetchedAt])

  /*
   * STABLE for the life of the page. React Router's `setSearchParams` changes identity whenever
   * the search params do, and this used to depend on it — so opening the drawer (`?jobId=`) made
   * a new `setParam`, a new `openJobFromRow`, a new QueueTable `columns`, and every row's cells
   * remounted. Measured on QA (2026-10-14): the "Actions for …" button that opened the drawer
   * was a detached node by the time the drawer closed, so focus fell to <body> (WCAG 2.4.3).
   * The latest setter is read through a ref, so its `prev` is still the current params.
   */
  const setSearchParamsRef = useRef(setSearchParams)
  useEffect(() => { setSearchParamsRef.current = setSearchParams })
  const setParam = useCallback((key, value) => {
    setSearchParamsRef.current((prev) => {
      const p = new URLSearchParams(prev)
      if (value) p.set(key, value); else p.delete(key)
      return p
    }, { replace: true })
  }, [])

  // Stable identities for everything handed to QueueTable. An inline arrow here
  // gives the table's `columns` memo a new dependency on every render, which
  // remounts each row's kebab menu — the same failure mode the page clock caused.
  const openJobFromRow = useCallback((j) => setParam('jobId', j.id), [setParam])
  const dismissJob = useCallback((j) => setConfirmDismiss(j), [])
  const closeDrawer = useCallback(() => setParam('jobId', ''), [setParam])

  /*
   * Cancel a live run — the REAL server route (spec 111).
   *
   * Distinct from `dismissJob` in both effect and word, and the distinction is
   * the whole point: dismiss removes the row and leaves the work running; cancel
   * marks the job `cancelled` so the runner abandons it and deletes its stored
   * result. The UI had only ever called the first, so "there is no honest cancel"
   * had been true in the client for weeks after it stopped being true in cb-api.
   *
   * The toast stops where the capability stops. CB has no cancel hook, so the
   * legacy query may run to completion on the database (cb-api
   * `jobs/routes.js:451` records this). Saying "cancelled" flatly would promise
   * something we cannot deliver on a report that is loading a firm's WIP.
   *
   * `refresh()` rather than optimistic local state: the row's real terminal state
   * comes from the server, and inventing `cancelled` locally is how a failed
   * cancel would look identical to a successful one.
   */
  const cancelRun = useCallback(async (j) => {
    if (!j?.id) return
    try {
      await reportJobService.cancel(j.id)
      toast.info('Stopped waiting. The database query may still finish on its own.')
    } catch {
      toast.error('That run could not be cancelled — it may already have finished.')
    } finally {
      refresh()
      setParam('jobId', '')
    }
  }, [refresh, setParam])

  const listJob = useMemo(() => allJobs.find((j) => j.id === openJobId) || null, [allJobs, openJobId])

  // `savedParams` (the run's actual filter values) lives ONLY on the status
  // endpoint, and the poller only calls that for in-flight jobs — so a finished
  // job's "Filters used" would always be empty without this one extra fetch when
  // the drawer opens.
  //
  // CC-50147 / QA TC3c — `isError` IS READ, and that is the whole fix. This query
  // used to destructure only `data`, so the genuine 404 from
  // `cb-api .../jobs/routes.js` ("Job not found", owner-scoped) was discarded, the
  // drawer's `if (!job) return null` rendered nothing, and `?jobId=<anything>`
  // showed the ordinary queue with no error, no empty state and no mention of the
  // run that had been asked for. This is the AUTHORITATIVE signal: it is
  // independent of what the list happens to have fetched, and it stays correct
  // when paging changes.
  //
  // `retry: false` because the app-wide default retries once, which would delay
  // the message by a round trip for a 404 that will never become a 200.
  const {
    data: jobDetail,
    isError: jobDetailFailed,
  } = useQuery({
    queryKey: ['report-job-detail', openJobId],
    queryFn: () => reportJobService.status(openJobId),
    enabled: Boolean(openJobId),
    staleTime: 30_000,
    retry: false,
  })

  // Two DIFFERENT failures, deliberately not conflated:
  //
  //  - the run does not resolve at all           -> `jobDetailFailed`, notice below
  //  - the run resolves but is not in this page  -> open the drawer from the detail
  //
  // The second is CC-53049's window: `QUEUE_PAGE_SIZE` is 50, so a valid older run
  // fails `allJobs.find(...)` and used to produce byte-identical silence. Treating
  // "not in allJobs" as "does not exist" is the specific mistake the diagnosis
  // warns against, and `mergeJob` already tolerates a null base
  // (`reportJobService.js` — `if (!base) return incoming`), so the detail read
  // alone is enough to open it.
  //
  // Spec 132 — and the opened job is NAMED here, whichever branch produced it. This
  // comment used to say "the drawer supplies its own name fallback"; that fallback was
  // the bug. A detail-only job never passed through the list's `named` memo, so it was
  // headed "Report 190", its PDF masthead and running foot said "Report 34" and it
  // saved as `report.pdf` — on every Schedules-page "Last run" link. `nameJob` is the
  // hook's own binder (same maps, same rule, stable identity), and since this memo is
  // the drawer's ONLY input, this one line names every entry point: row click, deep
  // link, Schedules "Last run", Run history "Open", and the confirm-dismiss text.
  //
  // Spec 137 (*review*) — the delivery outcome is the one group the detail must NOT
  // overwrite: the detail is read once, the list row every poll, so a drawer left open
  // through a retry used to keep the snapshot's attempts and error. `preferListDelivery`
  // takes the whole group from the polled row whenever it carries it.
  const openJob = useMemo(() => {
    const j = listJob ? preferListDelivery(mergeJob(listJob, jobDetail), listJob) : jobDetail
    return j ? nameJob(j) : null
  }, [listJob, jobDetail, nameJob])

  // --- "Queue a report" modal -------------------------------------------------
  // Opened from this page, so it carries the report picker; picking a report
  // fetches its definition to render that report's own filter controls. All of
  // that — the catalog options, the definition read, the filter seeding, both
  // guards and the create calls — now lives in `useQueueReportPicker`, which the
  // Manage Schedules page shares so its "New schedule" button opens this same
  // modal in place instead of navigating to the catalog.
  //
  // `initialWhen` is passed EXPLICITLY even though 'now' is the modal's own
  // default: this button says "Queue a report", the schedules page's says "New
  // schedule", and the two hosts differ in nothing else. Leaving it implicit is
  // what let a create promise get pasted onto a browse link once already.
  const picker = useQueueReportPicker({ initialWhen: 'now' })
  const openModal = picker.open

  // `schedulesQuery` is declared at the top of the component (spec 132 — it also
  // names runs); the note on why this page reads schedules at all is there.
  /*
   * CC-53132 — `needsSignIn` is the one state this control exists to surface, and it
   * is counted from a list. On an unresolved read that count is 0, which renders as a
   * bare "Manage schedules" — i.e. byte-identical to a firm with nothing dormant. The
   * whole credential design is built to make that state visible, so a silent zero here
   * defeats it.
   *
   * `idle` is NOT a failure here and must not be surfaced: the query is `enabled` off
   * whenever report jobs are off, so on such an environment it stays idle forever and
   * an "unavailable" note would be permanent — the page already says the queue isn't
   * enabled at all.
   *
   * The state comes from the hook rather than a second `useReadState` call here:
   * `useSchedules` already classifies it, and re-classifying would emit the permanent
   * blocked-read diagnostic twice for one read.
   */
  const schedulesUnreadable = schedulesQuery.state === 'blocked' || schedulesQuery.state === 'failed'
  const { schedules } = schedulesQuery
  // TWO distinct stopped states, counted separately (CC-50272). `enabled:false` is
  // the owner turning Active off; `needsSignIn` is a lost credential. This used to
  // pass only the `needsSignIn` count into a chip labelled "N paused", so pausing a
  // schedule showed PAUSED on the schedules page and nothing here — see the note on
  // ManageSchedulesLink.
  const scheduleCounts = useMemo(() => ({
    paused: schedules.filter((s) => !s.enabled).length,
    needsSignIn: schedules.filter((s) => s.needsSignIn).length,
  }), [schedules])

  /*
   * Q9 — "next scheduled run in 9 hours" in the subline (Himaad's Section 2).
   *
   * Answers a question the queue table structurally cannot: the next SCHEDULED
   * fire produces no row until it happens, so a firm with a nightly Trial Balance
   * and nothing else queued sees an empty queue and no sign that anything is
   * coming. That was already a live report ("a schedule I created doesn't appear")
   * and is why `ManageSchedulesLink` carries a count at all.
   *
   * Three things it deliberately will not do:
   *
   *   - It skips disabled schedules AND `needsSignIn` ones. A dormant schedule has
   *     a `nextRunAt` and will not fire — the cron skips it — so counting it here
   *     would be a promise the system has already broken. That state gets its own
   *     loud treatment on the Manage-schedules control; it must not also appear
   *     here as a healthy upcoming run.
   *   - It renders NOTHING when the schedules read failed or is unresolved.
   *     `schedules` is `[]` on a failed read, and "no upcoming runs" derived from
   *     a read that did not happen is the empty-vs-failed conflation this page has
   *     already been fixed for twice.
   *   - It ignores anything already in the past. A `nextRunAt` behind `now` means
   *     the cron has not caught up, not that a run is imminent.
   */
  const nextScheduledAt = useMemo(() => {
    if (schedulesUnreadable) return null
    const upcoming = schedules
      .filter((s) => s.enabled && !s.needsSignIn && s.nextRunAt != null && s.nextRunAt > now)
      .map((s) => s.nextRunAt)
    return upcoming.length ? Math.min(...upcoming) : null
  }, [schedules, schedulesUnreadable, now])

  /*
   * The same predicate, as a COUNT, for the Scheduled tab (QA, 2026-09-03).
   *
   * `useReportQueue` counts jobs and knows nothing about schedules, so its
   * `counts.scheduled` was the number of `state='scheduled'` jobs — which is
   * structurally 0 for anyone who uses recurring schedules rather than
   * queue-for-later (measured on QA D1: zero such rows have ever existed on
   * vladdemo, against 11 schedules). The tab therefore read `Scheduled 0`
   * directly under a subtitle saying "next scheduled run in 20 hours".
   *
   * Both populations are "going to run", which is what the label says, so the
   * badge counts both and `UpcomingScheduledRuns` names the difference on screen.
   *
   * `null` — not 0 — when the schedules read did not resolve, so the caller can
   * decline to assert a total it cannot back. Same rule as `countsResolved`.
   */
  const upcomingScheduledCount = useMemo(() => {
    if (schedulesUnreadable) return null
    return schedules.filter((s) => s.enabled && !s.needsSignIn && s.nextRunAt != null && s.nextRunAt > now).length
  }, [schedules, schedulesUnreadable, now])

  const tabCounts = useMemo(() => {
    if (!countsResolved) return null
    if (upcomingScheduledCount == null) return counts
    return { ...counts, scheduled: counts.scheduled + upcomingScheduledCount }
  }, [counts, countsResolved, upcomingScheduledCount])

  /**
   * Re-running belongs to the report page — it owns the filter state and the run
   * pipeline. But WHICH of the two things the user asked for has to travel with them.
   *
   * CC-53051: this used to take only `job` and always navigate to a bare `?jobId=`, and
   * every one of eight affordances called it — "Run again, same filters", "Open report &
   * edit filters", "Fix & retry", "Duplicate & edit", the expired panel's "Run again",
   * both failed-panel buttons. They were indistinguishable, and the comment above
   * claimed the job's filters were restored, which was not true: the report page's
   * restore effect then loaded the user's PERSISTED filters and auto-ran with those.
   *
   * Reproduced live on the deployed host: a run filtered 2019-02-23|2019-03-02 (52 rows)
   * re-ran as 2020-02-23|2020-03-24 (12 rows), and the fresh figures landed in the grid
   * under a banner reading "Saved result — as of …" belonging to the older run.
   *
   *   'rerun' → run it again now, with THIS job's filters (`?rerun=1`)
   *   'edit'  → open with this job's filters pre-filled and WAIT
   */
  const handleRerun = useCallback((job, intent = 'edit') => {
    if (!job?.reportId) return
    const rerun = intent === 'rerun' ? '&rerun=1' : ''
    navigate(`/reports/${job.reportId}?jobId=${job.id}${rerun}`)
  }, [navigate])

  // CC-53047: a failed read must not produce confident numbers. The user-visible half of
  // the identical bug on the catalog (CC-50321) was exactly this — the counts still read
  // 0 under an error panel, which is a claim, not an absence.
  const activeCount = listError ? null : (counts.running ?? 0)
  const readyToView = listError ? null : (counts.ready ?? 0)
  // Spec 152 — the results bucket's age comes from the list ENVELOPE (`resultMaxAgeDays`
  // above), not the rows. Read off the rows it was null for every first paint, and the
  // subtitle then said "results are kept 1 week" — five days longer than prod's bucket.
  // Spec 152 review: while it is unknown (no read yet, or none declared) the clause is
  // left out entirely; `queueRetentionSummary` never falls back to the row's week.
  const retentionSummary = queueRetentionSummary(resultMaxAgeDays)
  // "N of M runs shown" counts what can be LISTED: an expired run never is (Jordan,
  // 2026-08-07), so neither side may include one. `counts.all` already excludes them
  // when the worker classified them (spec 152 review); `total` is every run.
  const listedCount = useMemo(() => allJobs.filter((j) => j.effectiveState !== 'expired').length, [allJobs])
  const runsInScope = countsAreComplete ? counts.all : total

  /*
   * Q15's gate: a genuinely empty queue on a read that SUCCEEDED.
   *
   * All four conditions are load-bearing, and three of them are the same lesson:
   * `!listError` and `!loading` keep a failed or in-flight read from rendering as
   * "nothing queued", and `fetchedAt != null` keeps the very first paint — before
   * any read has resolved — from doing the same. `allJobs.length === 0` alone
   * would be true in every one of those cases.
   *
   * Deliberately NOT keyed on the `total` count: that is null on an older worker,
   * and "nothing queued" is a statement about the user's data, so it may only be
   * made from a read we know happened.
   */
  const isFirstRunEmpty = !listError && !loading && fetchedAt != null && allJobs.length === 0

  return (
    /*
     * A1 (2026-08-26) — a FRAGMENT, not a padded wrapper. This root was
     * `<div className="p-4 sm:p-6 lg:p-8">`, and commit 19f46be nested
     * `.report-shell` inside it instead of replacing it. The two paddings then
     * stacked: at >=1024px this page and Schedules were the only two Report
     * Center pages with 32 (wrapper) + 24 (main) + shell = 72px of gutter where
     * every other page had 40px. Worse, the wrapper sat OUTSIDE the shell's
     * max-width, so it also shifted the centring — the content column here was
     * both narrower and off-axis relative to every sibling page.
     *
     * The fragment keeps the fixed-position drawer and modals below as siblings
     * of the shell rather than inside its max-width box, which is where they
     * already were. `.report-shell` is now the ONLY width rule on this page.
     */
    <>
      {/* The Dashboards | Reports | Queue nav moved into the TOP BAR
          (ReportCenterNav, mounted by Header) — the Figma places it there, and
          three page-level copies of one nav had already drifted apart. */}

      <div className="report-shell">
        {/* Title zone: ONE loud thing. */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3.5">
          <div>
            {/* The In Progress badge that sat here claimed "scheduling,
                firm-wide visibility and real cancel are still being built".
                All three shipped (specs 057/058, CC-50150 part 2), so the
                badge had become false advertising in the other direction. */}
            <h1 className="font-display text-2xl text-gray-900">Report Queue</h1>
            <p className="mt-1.5 text-[11.5px] text-gray-500">
              {listError ? (
                <span>Counts unavailable — the queue couldn&apos;t be read</span>
              ) : isFirstRunEmpty ? (
                /*
                 * Q15 — Himaad's `Queue / Empty`. A first-time visitor was reading
                 * "0 running · 0 ready to view · results are kept 1 week": four
                 * facts about nothing, including a retention rule for results they
                 * do not have. The design says the one true thing instead.
                 *
                 * Gated on `!listError` by position (the branch above wins) AND on
                 * a RESOLVED read below, so a failed or in-flight read can never
                 * reach this and render "nothing queued" over an unknown queue.
                 */
                <span>Nothing queued right now</span>
              ) : (
                <>
                  {/* Himaad's subline order: running · ready · next scheduled ·
                      retention. `nextScheduledAt` is null unless there is a real
                      upcoming fire on a healthy schedule, so the clause simply
                      does not appear rather than saying "none". */}
                  {activeCount} running · {readyToView} ready to view
                  {nextScheduledAt ? ` · next scheduled run ${fmtInFuture(nextScheduledAt, now)}` : ''}
                  {/* Spec 152: "results are kept 1 week" was false for a schedule's runs
                      (kept by count, and by the bucket's age) and for everything on prod
                      (2 days). One sentence from the one retention module. */}
                  {retentionSummary ? ` · ${retentionSummary}` : ''}
                  {/* CC-53049: this said "showing your 25 most recent runs" — a hardcoded
                      number that was both stale and, once the counts moved server-side,
                      actively misleading about what the tab counts cover. Say what is
                      actually true: how many runs exist, and how many are on screen. */}
                  {/* Spec 152 §17 (live QA 2026-10-12): this span was `text-gray-400`,
                      #99a1af on the page ground it actually sits on — Layout's
                      `bg-gray-50` #f9fafb, nothing between paints one — = 2.490:1, under
                      SC 1.4.3's 4.5. It carries spec 152's corrected M, so it is a figure,
                      not decoration. `text-gray-500` #6a7282 on #f9fafb is 4.628:1: the
                      same grey as the rest of this subtitle, so the clause is no longer
                      lighter than its sentence. That margin is 0.128, and the test pins
                      it (components/reports/queue/__tests__/retentionContrast.test.js). */}
                  <span data-testid="queue-runs-shown" className="text-gray-500">
                    {total != null
                      ? ` · ${listedCount} of ${runsInScope} runs shown`
                      : ` · showing your ${QUEUE_PAGE_SIZE} most recent runs`}
                    {countsAreComplete === false && total != null ? ' · counts cover this page only' : ''}
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {schedulesUnreadable ? (
              /* CC-53132: the chips below are counts, so an unresolved read renders
                 NONE of them — which reads as "nothing is paused and nothing needs
                 signing in", the one thing this control exists to tell you. Say the
                 state is unknown instead, and offer the read again. The link itself
                 stays: the schedules page is still reachable and worth reaching. */
              <span role="alert" className="flex items-center gap-1.5 text-[11.5px] text-amber-900">
                Schedule status unknown
                <button
                  type="button"
                  onClick={() => schedulesQuery.refetch?.()}
                  className="font-semibold text-cb-accent-text underline"
                >
                  Try again
                </button>
              </span>
            ) : (
              <ManageSchedulesLink
                total={schedules.length}
                paused={scheduleCounts.paused}
                needsSignIn={scheduleCounts.needsSignIn}
              />
            )}
            {/* Spec 172 — report packs: several schedules, one email. */}
            <Link to="/reports/queue/packs" className="btn-cb-toolbar">Report packs</Link>
            <button type="button" onClick={openModal} className="btn-cb-primary">Queue a report</button>
          </div>
        </div>

        {/*
          CC-50147 / QA TC3c — an unresolvable `?jobId=` says so.
          A schedule row's "Last run" cell links here (`SchedulesTable.jsx`, and
          `ScheduleRunHistory.jsx` does too), and the ticket's own promise for that
          link is that a row is "a route into the result rather than a dead end". It
          was a dead end: the page rendered its ordinary list and never mentioned
          the run.

          THE COPY MUST NOT ASSERT NON-EXISTENCE. cb-api returns 404 both for an id
          that does not exist and for one that is not the caller's, so "that run no
          longer exists" would be a confident lie about a colleague's job. It also
          covers the case nothing ever nulls: `schedules.last_job_id` still points
          at a job row after the queue's Dismiss hard-deletes it, or after
          per-schedule retention prunes it — which is QA's item 3b, currently
          "not reproducing" rather than fixed. That link becomes a message instead
          of silence, which is the most this page can honestly do about it.

          Placed as a sibling ABOVE the card, deliberately: the empty/error branch
          inside the card and the subtitle counts are CC-53101's region in this same
          ticket pass, and two sessions editing one error path is how a page ends up
          with two different messages for one failure.
        */}
        {openJobId && jobDetailFailed && (
          /*
            ReportBand, not a bespoke coloured div — that component exists BECAUSE
            five shipped surfaces each rolled their own, and its `warn` variant is
            documented as reserved for "this did not do what you asked", which is
            exactly this. `role="alert"` rather than the default polite `status`:
            the user followed a link expecting a specific run and got a list, so the
            message should not wait for a pause in the screen reader's queue.
          */
          <ReportBand
            variant="warn"
            role="alert"
            className="mb-3"
            data-testid="queue-jobid-unresolved"
            onDismiss={closeDrawer}
          >
            {/* Spec 152 review: retention named too. A schedule's run is now deleted the
                moment N newer runs finish, so this is the commonest reason a link from a
                scheduled email no longer opens — and it was the one reason not given. */}
            We couldn&rsquo;t open the run <span className="font-mono">{String(openJobId).slice(0, 8)}</span>.
            It may have been dismissed, it may be older than what its schedule or the queue keeps,
            or it may belong to someone else. The rest of your queue is below and is unaffected.
          </ReportBand>
        )}

        <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
          {/* CC-53101: `counts` is passed only when a read actually produced it.
              On a failed or unresolved read the five badges used to render five
              confident zeros directly above this page's own "Counts unavailable
              — the queue couldn't be read", which is the same false claim the
              subtitle had already been fixed for. A 0 is a claim, not an absence. */}
          <QueueStateTabs
            active={tab}
            counts={tabCounts}
            onChange={(k) => setParam('state', k === 'all' ? '' : k)}
          />

          <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-gray-100 px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="queue-search" className="sr-only">Filter this queue</label>
              {/* 2026-08-28 — the three controls in this row were three different
                  boxes. Measured before the change: this input ~29px tall (no
                  height set; py-1.5 + normal line-height on 12.5px text), 8px
                  radius, 12.5px text; the scope <select> below 38px / 6px / 14px
                  off `.select-rc`; the window <select> ~29px / 8px / 12.5px
                  ad-hoc. Only the middle one was on the design system.

                  All three now resolve to 38px / 6px / 14px / --color-rc-control.
                  `.input-rc` is the rc text input but it is 30px by design (dense
                  grid-header boxes), so `.input-rc-lg` raises just this call site
                  to the standalone control size — see the note on it in
                  src/index.css and why it is a modifier and not an edit to
                  `.input-rc`.

                  EVERY geometry utility that stood here is DELETED, not paired:
                  `.input-rc` is unlayered and `rounded-lg` / `border` /
                  `border-rc-control` / `px-2.5` / `py-1.5` / `text-[12.5px]` /
                  `placeholder:text-gray-500` all lose to it, so keeping them
                  would have left six lines of dead code claiming to size a
                  control they do not touch. `w-56` survives — `.input-rc`
                  deliberately sets no width.

                  The focus pair went with them, and that is an UPGRADE, not a
                  loss. `focus:border-cb-accent` was dead anyway (`.input-rc`
                  declares the `border` shorthand) and #479eff is 2.762:1 — below
                  the 3.0 floor it would have replaced a 3.026:1 border with.
                  `focus:ring-cb-accent/20` did survive the cascade (no
                  box-shadow in `.input-rc`) but it is a 20%-alpha halo.
                  `.input-rc:focus-visible` rings 2px of #1d4ed8 at 6.702:1, and
                  it is the same ring `.select-rc` gives the two selects — so the
                  row now focuses identically in three places instead of two.

                  Contrast recomputed from src/index.css this session, against
                  the surface this row actually sits on (the card at :280 is
                  bg-white, and this row adds no background of its own):
                  --color-rc-control #8b95a5 on #fff = 3.026:1, clears SC 1.4.11.
                  On gray-50 it would be 2.896:1 and fail, so if this row is ever
                  tinted, recompute rather than assume. The card outline and the
                  divider above stay light on purpose — they identify nothing. */}
              <input
                id="queue-search"
                type="search"
                value={search}
                onChange={(e) => setParam('q', e.target.value)}
                placeholder="Filter this queue…"
                className="w-56 input-rc input-rc-lg"
              />
              {/* Live as of CC-50150 part 2: the server resolves the audience. */}
              <QueueScopeSelect value={scope} onChange={(v) => setParam('scope', v === 'mine' ? '' : v)} />
              {/* The server tells us when a group lookup failed and it fell back
                  to "mine" (`scopeResolved:false`). This page destructured that
                  flag and never rendered it, so the fallback was invisible: the
                  list showed only YOUR runs under a "My group" label, which is
                  indistinguishable from a quiet group. Exactly the failure the
                  backend added the flag to prevent. */}
              {scope === 'group' && scopeResolved === false && (
                <span
                  role="status"
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900"
                  title="Your group could not be resolved from Centerbase, so this list has fallen back to your own runs."
                >
                  Showing your runs only — group unavailable
                </span>
              )}
              <label htmlFor="queue-window" className="sr-only">Time window</label>
              {/* Bound to the EFFECTIVE window, not the raw `?days=` string: with an
                  unusable value the control used to render blank while the list was
                  filtered by something else entirely. A valid-but-unlisted value
                  (`?days=90`) still shows blank, exactly as before — it is genuinely
                  applied, so the select has nothing honest to point at. */}
              <select
                id="queue-window"
                value={windowDays === ALL_WINDOW ? ALL_WINDOW_PARAM : String(windowDays)}
                onChange={(e) => setParam('days', e.target.value === String(DEFAULT_WINDOW_DAYS) ? '' : e.target.value)}
                title={`Filters the runs already loaded — the server returns your ${QUEUE_PAGE_SIZE} most recent.`}
                /* `.select-rc`, the same class the scope select beside it already
                   wore. `rounded-lg border border-rc-control px-2.5 py-1.5
                   text-[12.5px] text-gray-700` are DELETED rather than paired:
                   `.select-rc` is unlayered and every one of them would have been
                   dead code. Nothing is lost in the swap — `text-gray-700` and
                   `.select-rc`'s `color: var(--color-rc-ink-2)` are the same
                   #374151, and the border was already --color-rc-control. What
                   changes is the geometry (38px / 6px / 14px, up from ~29px / 8px
                   / 12.5px), the custom chevron replacing the platform one, and a
                   real focus ring where there was only the UA default. */
                className="select-rc"
              >
                {/*
                  * Ordered by length of time, ascending (Jordan, 2026-09-03). The
                  * default (7) is no longer first, which is safe only because this
                  * select is CONTROLLED — `value` above keeps the effective window
                  * selected regardless of option order.
                  *
                  * "All loaded runs", not "All". This filter is a client-side array
                  * filter over the ONE page the server returned (at most
                  * QUEUE_PAGE_SIZE rows — cb-api's `listJobs` takes no date
                  * parameter and the client sends no cursor), so it can never mean
                  * "all your runs". On a tenant with 411 runs a bare "All" showing
                  * 50 rows asserts a completeness the page cannot deliver, and on a
                  * financial surface a reader who sees "All · 12 rows" will conclude
                  * 12 runs exist. The label composes with the two disclosures already
                  * on screen: this select's own title, and the subtitle's "N of M
                  * runs shown".
                  */}
                <option value="1">Today</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value={ALL_WINDOW_PARAM}>All loaded runs</option>
              </select>
            </div>
            {/* The grid's own toolbar carries the count — a second one here just
                repeats it. */}
          </div>

          {/* The Scheduled tab's OTHER population — recurring schedules that have
              not fired yet, which produce no job row until they do. Gated on a
              resolved read: an empty list built from a failed one is the
              empty-vs-failed conflation, and the band above already says the
              status is unknown in that case. */}
          {REPORT_JOBS && tab === 'scheduled' && !schedulesUnreadable && (
            <UpcomingScheduledRuns schedules={schedules} now={now} />
          )}

          {!REPORT_JOBS ? (
            <div className="px-5 py-16 text-center text-sm text-gray-500">
              The report queue isn&apos;t enabled in this environment.
            </div>
          ) : listError ? (
            /* CC-53047 — a failed read must NOT render as an empty queue.
               Every branch below is now gated on `!listError` as well as `!loading`,
               because the bug was not a missing error panel: it was that the EMPTY
               STATE and the COUNTS still rendered underneath one. The user-visible half
               of the same defect on the catalog (CC-50321) was the counts reading 0.
               Same shape as Reports.jsx: the container carries role="alert" so the
               message and the button are both inside the live region, the service owns
               the diagnosis sentence, and the page owns the framing. */
            <div role="alert" className="px-5 py-16 text-center">
              <p className="text-sm font-semibold text-gray-900">We couldn&apos;t load your queue</p>
              <p className="mx-auto mt-1.5 max-w-lg text-[12.5px] text-gray-500">
                {listError.message || 'The queue could not be read.'}
              </p>
              <p className="mx-auto mt-1.5 max-w-lg text-[11.5px] text-gray-500">
                This is not an empty queue — your runs are safe, we just couldn&apos;t read the list.
              </p>
              <button type="button" onClick={refresh} className="btn-cb-toolbar mt-3.5">Try again</button>
            </div>
          ) : !loading && counts.all === 0 ? (
            /* counts.all, not allJobs.length: expired runs are no longer listed,
               so a queue holding only expired rows is genuinely empty here and
               must offer "queue a report", not "clear your filter". */
            <JobEmptyState onQueueReport={openModal} />
          ) : !loading && jobs.length === 0 && tab === 'scheduled' && upcomingScheduledCount > 0 ? (
            /* The upcoming-runs section above is showing rows, so "Nothing in the
               queue matches that filter" would be contradicted on screen by the
               content directly above it — the shape of the original defect, just
               inverted. Say what the absence actually is: no run has been BOOKED
               for a specific moment, which is a different thing from no schedule. */
            <p className="px-5 py-10 text-center text-[12.5px] text-gray-500">
              Nothing is booked for a one-off run. Use <span className="font-semibold">Queue a report</span> and
              pick <span className="font-semibold">Once</span> to schedule a single run for a chosen moment.
            </p>
          ) : !loading && jobs.length === 0 ? (
            /*
              * The button says "Show everything in the queue" and, until the All
              * option existed, could not (QA, 2026-09-03). It cleared `state` and
              * `q` — often already empty, since the row can be reached by the time
              * window alone — and left `days` untouched, so a user filtered down to
              * Today clicked "show everything" and saw the same nothing. Clearing
              * `days` to '' would only widen to the 7-day DEFAULT, which is still a
              * filter; the button's own claim is only true at the unbounded window.
              */
            <JobEmptyState
              filtered
              onClearFilter={() => { setParam('state', ''); setParam('q', ''); setParam('days', ALL_WINDOW_PARAM) }}
            />
          ) : (
            <QueueTable
              jobs={jobs}
              loading={loading}
              emptyMessage="Nothing in your queue yet"
              onOpen={openJobFromRow}
              onDismiss={dismissJob}
              onRerun={handleRerun}
            />
          )}
        </div>
      </div>

      <ReportJobDrawer
        job={openJob}
        allJobs={allJobs}
        now={now}
        onClose={closeDrawer}
        onRerun={handleRerun}
        onDuplicate={(j) => handleRerun(j, 'edit')}
        onDismiss={dismissJob}
        onCancelRun={cancelRun}
      />

      {/* Every prop — including the two guards (`columnsError`, `unsupported`)
          that stop an unscoped financial run being booked — comes from the hook
          as one bundle. Re-spelling them per host is how the schedules page came
          to have no creation flow at all; see useQueueReportPicker.js.

          CC-53132: when the CATALOG read itself did not resolve there is nothing
          to pick from, and an empty picker is the lie. Say so instead. */}
      {picker.isOpen && (picker.catalogUnreadable ? (
        <QueueCatalogUnreadable
          state={picker.catalogState}
          error={picker.catalogError}
          onRetry={picker.retryCatalog}
          onClose={picker.close}
        />
      ) : (
        <QueueReportModal {...picker.modalProps} />
      ))}

      {confirmDismiss && (
        <ConfirmModal
          title="Dismiss this run?"
          // Deliberately "Dismiss", never "Cancel": DELETE removes the row but
          // does NOT stop the in-flight work or release the legacy process.
          // Promising cancellation we can't deliver would be worse than the
          // honest label (spec 056 §Open — real cancel is backend work).
          // Every job reaching here is named (the list's rows and the drawer's
          // `openJob` both pass through `nameJob`); the fallback only guarantees this
          // sentence can never print “undefined”.
          message={
            `“${confirmDismiss.displayName || jobDisplayName(confirmDismiss)}” will be removed from your queue. `
            + 'If it is still running, the work continues on the server — dismissing only clears it from this list.'
          }
          confirmLabel="Dismiss"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={() => { dismiss(confirmDismiss.id); setConfirmDismiss(null); setParam('jobId', '') }}
          onCancel={() => setConfirmDismiss(null)}
        />
      )}
    </>
  )
}

export default ReportQueue
